import LoggerService from "@bfx/logger";
import { BaseCubiqLogger } from "shared/broker-client-services/worker/BaseCubiqLogger";
import { REQUEST_LOG_PREFIX } from "shared/broker-client-services/worker/CubiqLogger.constants";
import { CubiqLoggerConfig } from "shared/broker-client-services/worker/CubiqLoggerConfig";
import {
  ActionType,
  AdminArbitraryRequestImpl,
  AdminChangeUserPwdRequestImpl,
  AdminCreateUpdateRequestImpl,
  AdminImportRequestImpl,
  AdminDeleteRequestImpl,
  AdminEnumRequestImpl,
  AdminListingRequestImpl,
  AmendMarkupLockRequestImpl,
  AmendMarkupSalesApprovalRequestImpl,
  ApplicationNotificationsFetchRequestImpl,
  AuthRequestImpl,
  AutoHedgingChangeStateRequestImpl,
  BenchmarkOrderRequestImpl,
  BlotterDefinitionDeleteRequestImpl,
  BlotterDefinitionListRequestImpl,
  BlotterDefinitionRequestImpl,
  BlotterFilterDeleteRequestImpl,
  BlotterFilterListRequestImpl,
  BlotterFilterRequestImpl,
  BroadcastRequestImpl,
  BulkAdminModelRequestImpl,
  BulkMarketDataRequestImpl,
  BulkOrderRequestImpl,
  CalculateMarkupRequestImpl,
  ChangePositionHedgingTypeRequestImpl,
  ClientContactDeleteRequestImpl,
  ClientContactUpdateRequestImpl,
  ClientTraderDeleteRequestImpl,
  ClientTraderUpdateRequestImpl,
  ConcreteEncodable,
  DataProfileRequestImpl,
  DataProfileType,
  DefaultAccountProfileMappingRequestImpl,
  DetailedOrderStatusRequestImpl,
  Encodable,
  FillLockRequestImpl,
  FillNowLockRequestImpl,
  FixingOrderCancellationRequestImpl,
  FixingOrderModificationRequestImpl,
  FixingOrderRequestImpl,
  FixingOrderStatusRequestImpl,
  FixingPriceRequestImpl,
  FixingRateRequirementLockRequestImpl,
  FixingRateRequirementUnlockRequestImpl,
  FixingStatusRequestImpl,
  FixingSummariesRequestImpl,
  FwdRollChangeRequestImpl,
  FwdRollSaveRequestImpl,
  GammaOrderRequestImpl,
  HolidayCalendarRequestImpl,
  InterventionQueryRequestImpl,
  LinkedOrderInfoModificationRequestImpl,
  LinkedOrderModificationRequestImpl,
  LinkedOrderRequestImpl,
  LockForFwdRollRequestImpl,
  LockType,
  LogonRequestImpl,
  Long,
  ManualPositionHedgingStatusRequestImpl,
  MarketDataRequestImpl,
  ModificationLockRequestImpl,
  NdfFixingDateLookupRequestImpl,
  OpsExceptionActionRequestImpl,
  OpsExceptionRequestImpl,
  OpsFilterCategoryRequestImpl,
  OrderAcceptanceRequestImpl,
  OrderAuditLookupRequestImpl,
  OrderCancellationRequestImpl,
  OrderCancellationSingleRequestImpl,
  OrderDeleteRequestImpl,
  OrderExportRequestImpl,
  OrderFillNowRequestImpl,
  OrderFillRequestImpl,
  OrderImportRequestImpl,
  OrderPauseResumeRequestImpl,
  OrderQueryRequestImpl,
  OrderReactivationRequestImpl,
  OrderRecapLookupRequestImpl,
  OrderSalesApprovalRequestImpl,
  OrderStatusRequestImpl,
  OrderTransferRequestImpl,
  PasswordChangeRequestImpl,
  PingTimerImpl,
  PlatformOrderQueryRequestImpl,
  PortfolioClearForwardPriceRequestImpl,
  PortfolioForwardPriceRequestImpl,
  PortfolioRetryForwardPriceRequestImpl,
  PositionBookFilterMatchRequestImpl,
  PositionBookRequestImpl,
  ProfileUpdateRequestImpl,
  PublicProfileUpdateRequestImpl,
  RemoteTelemetryUpdateRequestImpl,
  SavedProfileRequestImpl,
  SecureTokenRefreshRequestImpl,
  ServiceResponseImpl,
  SessionDisconnectRequestImpl,
  SessionInitialiserRequestImpl,
  StandardDate,
  StaticDataProfileRequestImpl,
  SystemControlUpdateRequestImpl,
  SystemMonitorHistoryRequestImpl,
  TelemetrySubscriptionCatalogueRequestImpl,
  TelemetrySubscriptionRequestImpl,
  Tenor,
  TerminateUserSessionRequestImpl,
  ToggleInflightStateRequestImpl,
  TradeActionRequestImpl,
  TradeQueryRequestImpl,
  TradeStatusRequestImpl,
  TradingCalendarRequestImpl,
  UnlockFwdRollRequestImpl,
  UnlockRequestImpl,
  UpdateClientCommentsRequestImpl,
  UserProfileChangeRequestImpl,
} from "shared/cubiq-model-multi-asset";
import { ManualNettingAction } from "shared/cubiq-model-multi-asset/model/enum/ManualNettingAction";
import { ManualNettingRequestImpl } from "shared/cubiq-model-multi-asset/model/impl/ManualNettingRequestImpl";

/**
 * Cubiq logger for UI requests - response messages of type SreviceResponseEventImpl are handled
 * by the CubiqResponseLogger class
 *
 * By default shares a common config with other loggers - message logging can be enabled/disabled by type
 * based on the message encoding type
 */
export class CubiqRequestLogger extends BaseCubiqLogger {
  constructor(config: CubiqLoggerConfig) {
    super(REQUEST_LOG_PREFIX, config);
  }

  public processRequest(request: Encodable) {
    let requestType = this.getLastClassPart(request.encodableType);

    if (!this.config.outputRequestType(requestType)) {
      // We don't to log this request
      return;
    }

    this.updateMessageId(request);

    switch (requestType) {
      case "AdminArbitraryRequestImpl":
        this.logAdminArbitraryRequest(request as AdminArbitraryRequestImpl);
        break;
      case "AdminChangeUserPwdRequestImpl":
        this.logAdminChangeUserPwdRequest(request as AdminChangeUserPwdRequestImpl);
        break;
      case "AdminCreateUpdateRequestImpl":
        this.logAdminCreateUpdateRequest(request as AdminCreateUpdateRequestImpl);
        break;
      case "AdminImportRequestImpl":
        this.logAdminImportRequest(request as AdminImportRequestImpl);
        break;
      case "AdminDeleteRequestImpl":
        this.logAdminDeleteRequest(request as AdminDeleteRequestImpl);
        break;
      case "AdminEnumRequestImpl":
        this.logAdminEnumRequest(request as AdminEnumRequestImpl);
        break;
      case "AdminListingRequestImpl":
        this.logAdminListingRequest(request as AdminListingRequestImpl);
        break;
      case "AmendMarkupLockRequestImpl":
        this.logAmendMarkupLockRequest(request as AmendMarkupLockRequestImpl);
        break;
      case "AmendMarkupSalesApprovalRequestImpl":
        this.logAmendMarkupSalesApprovalRequest(request as AmendMarkupSalesApprovalRequestImpl);
        break;
      case "ApplicationNotificationsFetchRequestImpl":
        this.logApplicationNotificationsFetchRequest(
          request as ApplicationNotificationsFetchRequestImpl,
        );
        break;
      case "AuthRequestImpl":
        this.logAuthRequest(request as AuthRequestImpl);
        break;
      case "AutoHedgingChangeStateRequestImpl":
        this.logAutoHedgingChangeStateRequest(request as AutoHedgingChangeStateRequestImpl);
        break;
      case "BenchmarkOrderRequestImpl":
        this.logBenchmarkOrderRequest(request as BenchmarkOrderRequestImpl);
        break;
      case "BlotterDefinitionDeleteRequestImpl":
        this.logBlotterDefinitionDeleteRequest(request as BlotterDefinitionDeleteRequestImpl);
        break;
      case "BlotterDefinitionListRequestImpl":
        this.logBlotterDefinitionListRequest(request as BlotterDefinitionListRequestImpl);
        break;
      case "BlotterDefinitionRequestImpl":
        this.logBlotterDefinitionRequest(request as BlotterDefinitionRequestImpl);
        break;
      case "BlotterFilterDeleteRequestImpl":
        this.logBlotterFilterDeleteRequest(request as BlotterFilterDeleteRequestImpl);
        break;
      case "BlotterFilterListRequestImpl":
        this.logBlotterFilterListRequest(request as BlotterFilterListRequestImpl);
        break;
      case "BlotterFilterRequestImpl":
        this.logBlotterFilterRequest(request as BlotterFilterRequestImpl);
        break;
      case "BroadcastRequestImpl":
        this.logBroadcastRequest(request as BroadcastRequestImpl);
        break;
      case "BulkAdminModelRequestImpl":
        this.logBulkAdminModelRequest(request as BulkAdminModelRequestImpl);
        break;
      case "BulkMarketDataRequestImpl":
        this.logBulkMarketDataRequest(request as BulkMarketDataRequestImpl);
        break;
      case "BulkOrderRequestImpl":
        this.logBulkOrderRequest(request as BulkOrderRequestImpl);
        break;
      case "CalculateMarkupRequestImpl":
        this.logCalculateMarkupRequest(request as CalculateMarkupRequestImpl);
        break;
      case "ChangePositionHedgingTypeRequestImpl":
        this.logChangePositionHedgingTypeRequest(request as ChangePositionHedgingTypeRequestImpl);
        break;
      case "ClientContactDeleteRequestImpl":
        this.logClientContactDeleteRequest(request as ClientContactDeleteRequestImpl);
        break;
      case "ClientContactUpdateRequestImpl":
        this.logClientContactUpdateRequest(request as ClientContactUpdateRequestImpl);
        break;
      case "ClientTraderDeleteRequestImpl":
        this.logClientTraderDeleteRequest(request as ClientTraderDeleteRequestImpl);
        break;
      case "ClientTraderUpdateRequestImpl":
        this.logClientTraderUpdateRequest(request as ClientTraderUpdateRequestImpl);
        break;
      case "DataProfileRequestImpl":
        this.logDataProfileRequest(request as DataProfileRequestImpl);
        break;
      case "DefaultAccountProfileMappingRequestImpl":
        this.logDefaultAccountProfileMappingRequest(
          request as DefaultAccountProfileMappingRequestImpl,
        );
        break;
      case "DetailedOrderStatusRequestImpl":
        this.logDetailedOrderStatusRequest(request as DetailedOrderStatusRequestImpl);
        break;
      case "FillLockRequestImpl":
        this.logFillLockRequest(request as FillLockRequestImpl);
        break;
      case "FillNowLockRequestImpl":
        this.logFillNowLockRequest(request as FillNowLockRequestImpl);
        break;
      case "FixingOrderCancellationRequestImpl":
        this.logFixingOrderCancellationRequest(request as FixingOrderCancellationRequestImpl);
        break;
      case "FixingOrderModificationRequestImpl":
        this.logFixingOrderModificationRequest(request as FixingOrderModificationRequestImpl);
        break;
      case "FixingOrderRequestImpl":
        this.logFixingOrderRequest(request as FixingOrderRequestImpl);
        break;
      case "FixingOrderStatusRequestImpl":
        this.logFixingOrderStatusRequest(request as FixingOrderStatusRequestImpl);
        break;
      case "FixingPriceRequestImpl":
        this.logFixingPriceRequest(request as FixingPriceRequestImpl);
        break;
      case "FixingRateRequirementLockRequestImpl":
        this.logFixingRateRequirementLockRequest(request as FixingRateRequirementLockRequestImpl);
        break;
      case "FixingRateRequirementUnlockRequestImpl":
        this.logFixingRateRequirementUnlockRequest(
          request as FixingRateRequirementUnlockRequestImpl,
        );
        break;
      case "FixingStatusRequestImpl":
        this.logFixingStatusRequest(request as FixingStatusRequestImpl);
        break;
      case "FixingSummariesRequestImpl":
        this.logFixingSummariesRequest(request as FixingSummariesRequestImpl);
        break;
      case "FwdRollChangeRequestImpl":
        this.logFwdRollChangeRequest(request as FwdRollChangeRequestImpl);
        break;
      case "FwdRollSaveRequestImpl":
        this.logFwdRollSaveRequest(request as FwdRollSaveRequestImpl);
        break;
      case "GammaOrderRequestImpl":
        this.logGammaOrderRequest(request as GammaOrderRequestImpl);
        break;
      case "HolidayCalendarRequestImpl":
        this.logHolidayCalendarRequest(request as HolidayCalendarRequestImpl);
        break;
      case "InterventionQueryRequestImpl":
        this.logInterventionQueryRequest(request as InterventionQueryRequestImpl);
        break;
      case "LinkedOrderInfoModificationRequestImpl":
        this.logLinkedOrderInfoModificationRequest(
          request as LinkedOrderInfoModificationRequestImpl,
        );
        break;
      case "LinkedOrderModificationRequestImpl":
        this.logLinkedOrderModificationRequest(request as LinkedOrderModificationRequestImpl);
        break;
      case "LinkedOrderRequestImpl":
        this.logLinkedOrderRequest(request as LinkedOrderRequestImpl);
        break;
      case "LockForFwdRollRequestImpl":
        this.logLockForFwdRollRequest(request as LockForFwdRollRequestImpl);
        break;
      case "LogonRequestImpl":
        this.logLogonRequest(request as LogonRequestImpl);
        break;
      case "ManualPositionHedgingStatusRequestImpl":
        this.logManualPositionHedgingStatusRequest(
          request as ManualPositionHedgingStatusRequestImpl,
        );
        break;
      case "MarketDataRequestImpl":
        this.logMarketDataRequest(request as MarketDataRequestImpl);
        break;
      case "ModificationLockRequestImpl":
        this.logModificationLockRequest(request as ModificationLockRequestImpl);
        break;
      case "NdfFixingDateLookupRequestImpl":
        this.logNdfFixingDateLookupRequest(request as NdfFixingDateLookupRequestImpl);
        break;
      case "OpsExceptionActionRequestImpl":
        this.logOpsExceptionActionRequest(request as OpsExceptionActionRequestImpl);
        break;
      case "OpsExceptionRequestImpl":
        this.logOpsExceptionRequest(request as OpsExceptionRequestImpl);
        break;
      case "OpsFilterCategoryRequestImpl":
        this.logOpsFilterCategoryRequest(request as OpsFilterCategoryRequestImpl);
        break;
      case "OrderAcceptanceRequestImpl":
        this.logOrderAcceptanceRequest(request as OrderAcceptanceRequestImpl);
        break;
      case "OrderAuditLookupRequestImpl":
        this.logOrderAuditLookupRequest(request as OrderAuditLookupRequestImpl);
        break;
      case "OrderCancellationRequestImpl":
        this.logOrderCancellationRequest(request as OrderCancellationRequestImpl);
        break;
      case "OrderCancellationSingleRequestImpl":
        this.logOrderCancellationSingleRequest(request as OrderCancellationSingleRequestImpl);
        break;
      case "OrderDeleteRequestImpl":
        this.logOrderDeleteRequest(request as OrderDeleteRequestImpl);
        break;
      case "OrderExportRequestImpl":
        this.logOrderExportRequest(request as OrderExportRequestImpl);
        break;
      case "OrderFillNowRequestImpl":
        this.logOrderFillNowRequest(request as OrderFillNowRequestImpl);
        break;
      case "OrderFillRequestImpl":
        this.logOrderFillRequest(request as OrderFillRequestImpl);
        break;
      case "OrderImportRequestImpl":
        this.logOrderImportRequest(request as OrderImportRequestImpl);
        break;
      case "OrderPauseResumeRequestImpl":
        this.logOrderPauseResumeRequest(request as OrderPauseResumeRequestImpl);
        break;
      case "OrderQueryRequestImpl":
        this.logOrderQueryRequest(request as OrderQueryRequestImpl);
        break;
      case "OrderReactivationRequestImpl":
        this.logOrderReactivationRequest(request as OrderReactivationRequestImpl);
        break;
      case "OrderRecapLookupRequestImpl":
        this.logOrderRecapLookupRequest(request as OrderRecapLookupRequestImpl);
        break;
      case "OrderSalesApprovalRequestImpl":
        this.logOrderSalesApprovalRequest(request as OrderSalesApprovalRequestImpl);
        break;
      case "OrderStatusRequestImpl":
        this.logOrderStatusRequest(request as OrderStatusRequestImpl);
        break;
      case "OrderTransferRequestImpl":
        this.logOrderTransferRequest(request as OrderTransferRequestImpl);
        break;
      case "PasswordChangeRequestImpl":
        this.logPasswordChangeRequest(request as PasswordChangeRequestImpl);
        break;
      case "PlatformOrderQueryRequestImpl":
        this.logPlatformOrderQueryRequest(request as PlatformOrderQueryRequestImpl);
        break;
      case "PortfolioClearForwardPriceRequestImpl":
        this.logPortfolioClearForwardPriceRequest(request as PortfolioClearForwardPriceRequestImpl);
        break;
      case "PortfolioForwardPriceRequestImpl":
        this.logPortfolioForwardPriceRequest(request as PortfolioForwardPriceRequestImpl);
        break;
      case "PortfolioRetryForwardPriceRequestImpl":
        this.logPortfolioRetryForwardPriceRequest(request as PortfolioRetryForwardPriceRequestImpl);
        break;
      case "PositionBookFilterMatchRequestImpl":
        this.logPositionBookFilterMatchRequest(request as PositionBookFilterMatchRequestImpl);
        break;
      case "PositionBookRequestImpl":
        this.logPositionBookRequest(request as PositionBookRequestImpl);
        break;
      case "ProfileUpdateRequestImpl":
        this.logProfileUpdateRequest(request as ProfileUpdateRequestImpl);
        break;
      case "PublicProfileUpdateRequestImpl":
        this.logPublicProfileUpdateRequest(request as PublicProfileUpdateRequestImpl);
        break;
      case "RemoteTelemetryUpdateRequestImpl":
        this.logRemoteTelemetryUpdateRequest(request as RemoteTelemetryUpdateRequestImpl);
        break;
      case "SavedProfileRequestImpl":
        this.logSavedProfileRequest(request as SavedProfileRequestImpl);
        break;
      case "SecureTokenRefreshRequestImpl":
        this.logSecureTokenRefreshRequest(request as SecureTokenRefreshRequestImpl);
        break;
      case "SessionDisconnectRequestImpl":
        this.logSessionDisconnectRequest(request as SessionDisconnectRequestImpl);
        break;
      case "SessionInitialiserRequestImpl":
        this.logSessionInitialiserRequest(request as SessionInitialiserRequestImpl);
        break;
      case "StaticDataProfileRequestImpl":
        this.logStaticDataProfileRequest(request as StaticDataProfileRequestImpl);
        break;
      case "SystemControlUpdateRequestImpl":
        this.logSystemControlUpdateRequest(request as SystemControlUpdateRequestImpl);
        break;
      case "SystemMonitorHistoryRequestImpl":
        this.logSystemMonitorHistoryRequest(request as SystemMonitorHistoryRequestImpl);
        break;
      case "TelemetrySubscriptionCatalogueRequestImpl":
        this.logTelemetrySubscriptionCatalogueRequest(
          request as TelemetrySubscriptionCatalogueRequestImpl,
        );
        break;
      case "TelemetrySubscriptionRequestImpl":
        this.logTelemetrySubscriptionRequest(request as TelemetrySubscriptionRequestImpl);
        break;
      case "TerminateUserSessionRequestImpl":
        this.logTerminateUserSessionRequest(request as TerminateUserSessionRequestImpl);
        break;
      case "ToggleInflightStateRequestImpl":
        this.logToggleInflightStateRequest(request as ToggleInflightStateRequestImpl);
        break;
      case "TradeActionRequestImpl":
        this.logTradeActionRequest(request as TradeActionRequestImpl);
        break;
      case "TradeQueryRequestImpl":
        this.logTradeQueryRequest(request as TradeQueryRequestImpl);
        break;
      case "TradeStatusRequestImpl":
        this.logTradeStatusRequest(request as TradeStatusRequestImpl);
        break;
      case "TradingCalendarRequestImpl":
        this.logTradingCalendarRequest(request as TradingCalendarRequestImpl);
        break;
      case "UnlockFwdRollRequestImpl":
        this.logUnlockFwdRollRequest(request as UnlockFwdRollRequestImpl);
        break;
      case "UnlockRequestImpl":
        this.logUnlockRequest(request as UnlockRequestImpl);
        break;
      case "UpdateClientCommentsRequestImpl":
        this.logUpdateClientCommentsRequest(request as UpdateClientCommentsRequestImpl);
        break;
      case "UserProfileChangeRequestImpl":
        this.logUserProfileChangeRequest(request as UserProfileChangeRequestImpl);
        break;
      case "ManualNettingRequestImpl":
        this.logManualNettingRequest(request as ManualNettingRequestImpl);
        break;

      // Those Requests that don't end in RequestImpl!
      case "PingTimerImpl":
        this.logPingTimer(request as PingTimerImpl);
        break;
      default:
        this.logUnsupported("Request", requestType);
        break;
    }
  }

  private logAdminArbitraryRequest(_request: AdminArbitraryRequestImpl) {
    this.log(`AdminArbitraryRequest`);
  }
  private logAdminChangeUserPwdRequest(_request: AdminChangeUserPwdRequestImpl) {
    this.log(`AdminChangeUserPwdRequest`);
  }
  private logAdminCreateUpdateRequest(_request: AdminCreateUpdateRequestImpl) {
    this.log(`AdminCreateUpdateRequest`);
  }
  private logAdminImportRequest(_request: AdminImportRequestImpl) {
    this.log(`AdminImportRequestImpl`);
  }
  private logAdminDeleteRequest(_request: AdminDeleteRequestImpl) {
    this.log(`AdminDeleteRequest`);
  }
  private logAdminEnumRequest(_request: AdminEnumRequestImpl) {
    this.log(`AdminEnumRequest`);
  }
  private logAdminListingRequest(request: AdminListingRequestImpl) {
    this.log(`AdminListingRequest - for entity type=${request.entityType}`);
  }
  private logAmendMarkupLockRequest(request: AmendMarkupLockRequestImpl) {
    const lockType = LockType[request.lockType];
    const orderInfo = this.getOrderId(request.orderId);
    this.log(`AmendMarkupLockRequest (${lockType}) - ${orderInfo}`);
  }
  private logAmendMarkupSalesApprovalRequest(_request: AmendMarkupSalesApprovalRequestImpl) {
    this.log(`AmendMarkupSalesApprovalRequest`);
  }
  private logApplicationNotificationsFetchRequest(
    _request: ApplicationNotificationsFetchRequestImpl,
  ) {
    this.log(`ApplicationNotificationsFetchRequest`);
  }
  private logAuthRequest(_request: AuthRequestImpl) {
    this.log(`AuthRequest`);
  }
  private logAutoHedgingChangeStateRequest(_request: AutoHedgingChangeStateRequestImpl) {
    this.log(`AutoHedgingChangeStateRequest`);
  }
  private logBenchmarkOrderRequest(_request: BenchmarkOrderRequestImpl) {
    this.log(`BenchmarkOrderRequest`);
  }
  private logBlotterDefinitionDeleteRequest(_request: BlotterDefinitionDeleteRequestImpl) {
    this.log(`BlotterDefinitionDeleteRequest`);
  }
  private logBlotterDefinitionListRequest(_request: BlotterDefinitionListRequestImpl) {
    this.log(`BlotterDefinitionListRequest`);
  }
  private logBlotterDefinitionRequest(_request: BlotterDefinitionRequestImpl) {
    this.log(`BlotterDefinitionRequest`);
  }
  private logBlotterFilterDeleteRequest(_request: BlotterFilterDeleteRequestImpl) {
    this.log(`BlotterFilterDeleteRequest`);
  }
  private logBlotterFilterListRequest(_request: BlotterFilterListRequestImpl) {
    this.log(`BlotterFilterListRequest`);
  }
  private logBlotterFilterRequest(_request: BlotterFilterRequestImpl) {
    this.log(`BlotterFilterRequest`);
  }
  private logBroadcastRequest(_request: BroadcastRequestImpl) {
    this.log(`BroadcastRequest`);
  }
  private logBulkAdminModelRequest(_request: BulkAdminModelRequestImpl) {
    this.log(`BulkAdminModelRequest`);
  }
  private logBulkMarketDataRequest(_request: BulkMarketDataRequestImpl) {
    this.log(`BulkMarketDataRequest`);
  }
  private logBulkOrderRequest(_request: BulkOrderRequestImpl) {
    this.log(`BulkOrderRequest`);
  }
  private logCalculateMarkupRequest(_request: CalculateMarkupRequestImpl) {
    this.log(`CalculateMarkupRequest`);
  }
  private logChangePositionHedgingTypeRequest(_request: ChangePositionHedgingTypeRequestImpl) {
    this.log(`ChangePositionHedgingTypeRequest`);
  }
  private logClientContactDeleteRequest(_request: ClientContactDeleteRequestImpl) {
    this.log(`ClientContactDeleteRequest`);
  }
  private logClientContactUpdateRequest(_request: ClientContactUpdateRequestImpl) {
    this.log(`ClientContactUpdateRequest`);
  }
  private logClientTraderDeleteRequest(_request: ClientTraderDeleteRequestImpl) {
    this.log(`ClientTraderDeleteRequest`);
  }
  private logClientTraderUpdateRequest(_request: ClientTraderUpdateRequestImpl) {
    this.log(`ClientTraderUpdateRequest`);
  }
  private logDataProfileRequest(request: DataProfileRequestImpl) {
    const profileDescriptors =
      request.profileDescriptors?.map((descriptor) => `name:${descriptor.filterName}`) ?? [];
    this.log(`DataProfileRequest - [${profileDescriptors.join(", ")}]`);
  }

  private logDefaultAccountProfileMappingRequest(
    _request: DefaultAccountProfileMappingRequestImpl,
  ) {
    this.log(`DefaultAccountProfileMappingRequest`);
  }
  private logDetailedOrderStatusRequest(request: DetailedOrderStatusRequestImpl) {
    this.log(`DetailedOrderStatusRequest - order[${request.orderId}]`);
  }
  private logFillLockRequest(request: FillLockRequestImpl) {
    const lockType = LockType[request.lockType];
    const orderInfo = this.getOrderIds(request.orderIds);
    this.log(`FillLockRequest (${lockType}) - ${orderInfo}`);
  }
  private logFillNowLockRequest(request: FillNowLockRequestImpl) {
    const lockType = LockType[request.lockType];
    const orderInfo = this.getOrderId(request.orderId);
    this.log(`FillNowLockRequest(${lockType}) - ${orderInfo}`);
  }
  private logFixingOrderCancellationRequest(_request: FixingOrderCancellationRequestImpl) {
    this.log(`FixingOrderCancellationRequest`);
  }
  private logFixingOrderModificationRequest(_request: FixingOrderModificationRequestImpl) {
    this.log(`FixingOrderModificationRequest`);
  }
  private logFixingOrderRequest(_request: FixingOrderRequestImpl) {
    this.log(`FixingOrderRequest`);
  }
  private logFixingOrderStatusRequest(_request: FixingOrderStatusRequestImpl) {
    this.log(`FixingOrderStatusRequest`);
  }
  private logFixingPriceRequest(_request: FixingPriceRequestImpl) {
    this.log(`FixingPriceRequest`);
  }
  private logFixingRateRequirementLockRequest(_request: FixingRateRequirementLockRequestImpl) {
    this.log(`FixingRateRequirementLockRequest`);
  }
  private logFixingRateRequirementUnlockRequest(_request: FixingRateRequirementUnlockRequestImpl) {
    this.log(`FixingRateRequirementUnlockRequest`);
  }
  private logFixingStatusRequest(_request: FixingStatusRequestImpl) {
    this.log(`FixingStatusRequest`);
  }
  private logFixingSummariesRequest(_request: FixingSummariesRequestImpl) {
    this.log(`FixingSummariesRequest`);
  }
  private logFwdRollChangeRequest(_request: FwdRollChangeRequestImpl) {
    this.log(`FwdRollChangeRequest`);
  }
  private logFwdRollSaveRequest(_request: FwdRollSaveRequestImpl) {
    this.log(`FwdRollSaveRequest`);
  }
  private logGammaOrderRequest(_request: GammaOrderRequestImpl) {
    this.log(`GammaOrderRequest`);
  }
  private logHolidayCalendarRequest(_request: HolidayCalendarRequestImpl) {
    this.log(`HolidayCalendarRequest`);
  }
  private logInterventionQueryRequest(_request: InterventionQueryRequestImpl) {
    this.log(`InterventionQueryRequest`);
  }
  private logLinkedOrderInfoModificationRequest(_request: LinkedOrderInfoModificationRequestImpl) {
    this.log(`LinkedOrderInfoModificationRequest`);
  }
  private logLinkedOrderModificationRequest(_request: LinkedOrderModificationRequestImpl) {
    this.log(`LinkedOrderModificationRequest`);
  }
  private logLinkedOrderRequest(_request: LinkedOrderRequestImpl) {
    this.log(`LinkedOrderRequest`);
  }
  private logLockForFwdRollRequest(_request: LockForFwdRollRequestImpl) {
    this.log(`LockForFwdRollRequest`);
  }
  private logLogonRequest(_request: LogonRequestImpl) {
    this.log(`LogonRequest`);
  }
  private logManualPositionHedgingStatusRequest(_request: ManualPositionHedgingStatusRequestImpl) {
    this.log(`ManualPositionHedgingStatusRequest`);
  }
  private logMarketDataRequest(request: MarketDataRequestImpl) {
    const parts: string[] = [];
    parts.push(ActionType[request.actionType]);
    parts.push(request.subscriptionCode);
    parts.push(request.subscriptionClass);
    if (typeof request.tenor === "number" && request.tenor !== Tenor.EMPTY) {
      parts.push(`Tenor(${Tenor[request.tenor]})`);
    }
    if (request.valueDate) {
      parts.push(this.addStandardDate("ValueDate", request.valueDate));
    }
    const partsText = parts.join(" ");

    this.log(`MarketDataRequest - ${partsText}`);
  }
  private logModificationLockRequest(request: ModificationLockRequestImpl) {
    const lockType = LockType[request.lockType];
    const orderInfo = this.getOrderId(request.orderId);
    this.log(`ModificationLockRequest (${lockType}) - ${orderInfo}`);
  }
  private logNdfFixingDateLookupRequest(_request: NdfFixingDateLookupRequestImpl) {
    this.log(`NdfFixingDateLookupRequest`);
  }
  private logOpsExceptionActionRequest(_request: OpsExceptionActionRequestImpl) {
    this.log(`OpsExceptionActionRequest`);
  }
  private logOpsExceptionRequest(_request: OpsExceptionRequestImpl) {
    this.log(`OpsExceptionRequest`);
  }
  private logOpsFilterCategoryRequest(_request: OpsFilterCategoryRequestImpl) {
    this.log(`OpsFilterCategoryRequest`);
  }
  private logOrderAcceptanceRequest(_request: OrderAcceptanceRequestImpl) {
    this.log(`OrderAcceptanceRequest`);
  }
  private logOrderAuditLookupRequest(_request: OrderAuditLookupRequestImpl) {
    this.log(`OrderAuditLookupRequest`);
  }
  private logOrderCancellationRequest(_request: OrderCancellationRequestImpl) {
    this.log(`OrderCancellationRequest`);
  }
  private logOrderCancellationSingleRequest(_request: OrderCancellationSingleRequestImpl) {
    this.log(`OrderCancellationSingleRequest`);
  }
  private logOrderDeleteRequest(_request: OrderDeleteRequestImpl) {
    this.log(`OrderDeleteRequest`);
  }
  private logOrderExportRequest(_request: OrderExportRequestImpl) {
    this.log(`OrderExportRequest`);
  }
  private logOrderFillNowRequest(_request: OrderFillNowRequestImpl) {
    this.log(`OrderFillNowRequest`);
  }
  private logOrderFillRequest(_request: OrderFillRequestImpl) {
    this.log(`OrderFillRequest`);
  }
  private logOrderImportRequest(_request: OrderImportRequestImpl) {
    this.log(`OrderImportRequest`);
  }
  private logOrderPauseResumeRequest(_request: OrderPauseResumeRequestImpl) {
    this.log(`OrderPauseResumeRequest`);
  }
  private logOrderQueryRequest(_request: OrderQueryRequestImpl) {
    this.log(`OrderQueryRequest`);
  }
  private logOrderReactivationRequest(_request: OrderReactivationRequestImpl) {
    this.log(`OrderReactivationRequest`);
  }
  private logOrderRecapLookupRequest(_request: OrderRecapLookupRequestImpl) {
    this.log(`OrderRecapLookupRequest`);
  }
  private logOrderSalesApprovalRequest(_request: OrderSalesApprovalRequestImpl) {
    this.log(`OrderSalesApprovalRequest`);
  }
  private logOrderStatusRequest(_request: OrderStatusRequestImpl) {
    this.log(`OrderStatusRequest`);
  }
  private logOrderTransferRequest(_request: OrderTransferRequestImpl) {
    this.log(`OrderTransferRequest`);
  }
  private logPasswordChangeRequest(_request: PasswordChangeRequestImpl) {
    this.log(`PasswordChangeRequest`);
  }
  private logPlatformOrderQueryRequest(_request: PlatformOrderQueryRequestImpl) {
    this.log(`PlatformOrderQueryRequest`);
  }
  private logPortfolioClearForwardPriceRequest(_request: PortfolioClearForwardPriceRequestImpl) {
    this.log(`PortfolioClearForwardPriceRequest`);
  }
  private logPortfolioForwardPriceRequest(_request: PortfolioForwardPriceRequestImpl) {
    this.log(`PortfolioForwardPriceRequest`);
  }
  private logPortfolioRetryForwardPriceRequest(_request: PortfolioRetryForwardPriceRequestImpl) {
    this.log(`PortfolioRetryForwardPriceRequest`);
  }
  private logPositionBookFilterMatchRequest(_request: PositionBookFilterMatchRequestImpl) {
    this.log(`PositionBookFilterMatchRequest`);
  }
  private logPositionBookRequest(request: PositionBookRequestImpl) {
    const parts = [];
    if (request.baseCurrencyCd) {
      parts.push(`Pair[${request.baseCurrencyCd}${request.nonBaseCurrencyCd}]`);
    }
    if (request.branchCd) {
      parts.push(`Branch[${request.branchCd}]`);
    } else if (request.traderGroupId) {
      parts.push(`TraderGroup[${request.traderGroupId.toString()}`);
      parts.push(`Vehicle[${request.crossCcy}`);
    }
    const partsText = parts.join(" ");
    this.log(`PositionBookRequest - ${partsText}`);
  }
  private logProfileUpdateRequest(_request: ProfileUpdateRequestImpl) {
    this.log(`ProfileUpdateRequest`);
  }
  private logPublicProfileUpdateRequest(_request: PublicProfileUpdateRequestImpl) {
    this.log(`PublicProfileUpdateRequest`);
  }
  private logRemoteTelemetryUpdateRequest(_request: RemoteTelemetryUpdateRequestImpl) {
    this.log(`RemoteTelemetryUpdateRequest`);
  }
  private logSavedProfileRequest(_request: SavedProfileRequestImpl) {
    this.log(`SavedProfileRequest`);
  }
  private logSecureTokenRefreshRequest(_request: SecureTokenRefreshRequestImpl) {
    this.log(`SecureTokenRefreshRequest`);
  }
  private logSessionDisconnectRequest(_request: SessionDisconnectRequestImpl) {
    this.log(`SessionDisconnectRequest`);
  }
  private logSessionInitialiserRequest(_request: SessionInitialiserRequestImpl) {
    this.log(`SessionInitialiserRequest`);
  }
  private logStaticDataProfileRequest(request: StaticDataProfileRequestImpl) {
    const profileMask = request.dataMask?.toString(16) ?? "NONE";
    this.log(`StaticDataProfileRequest - mask = ${profileMask}`);
  }
  private logSystemControlUpdateRequest(_request: SystemControlUpdateRequestImpl) {
    this.log(`SystemControlUpdateRequest`);
  }
  private logSystemMonitorHistoryRequest(_request: SystemMonitorHistoryRequestImpl) {
    this.log(`SystemMonitorHistoryRequest`);
  }
  private logTelemetrySubscriptionCatalogueRequest(
    _request: TelemetrySubscriptionCatalogueRequestImpl,
  ) {
    this.log(`TelemetrySubscriptionCatalogueRequest`);
  }
  private logTelemetrySubscriptionRequest(_request: TelemetrySubscriptionRequestImpl) {
    this.log(`TelemetrySubscriptionRequest`);
  }
  private logTerminateUserSessionRequest(_request: TerminateUserSessionRequestImpl) {
    this.log(`TerminateUserSessionRequest`);
  }
  private logToggleInflightStateRequest(_request: ToggleInflightStateRequestImpl) {
    this.log(`ToggleInflightStateRequest`);
  }
  private logTradeActionRequest(_request: TradeActionRequestImpl) {
    this.log(`TradeActionRequest`);
  }
  private logTradeQueryRequest(_request: TradeQueryRequestImpl) {
    this.log(`TradeQueryRequest`);
  }
  private logTradeStatusRequest(_request: TradeStatusRequestImpl) {
    this.log(`TradeStatusRequest`);
  }
  private logTradingCalendarRequest(_request: TradingCalendarRequestImpl) {
    this.log(`TradingCalendarRequest`);
  }
  private logUnlockFwdRollRequest(_request: UnlockFwdRollRequestImpl) {
    this.log(`UnlockFwdRollRequest`);
  }
  private logUnlockRequest(request: UnlockRequestImpl) {
    const lockType = LockType[request.lockType];
    const orderInfo = this.getOrderIds(request.orderIds);
    this.log(`UnlockRequest (${lockType}) - ${orderInfo}`);
  }
  private logUpdateClientCommentsRequest(_request: UpdateClientCommentsRequestImpl) {
    this.log(`UpdateClientCommentsRequest`);
  }

  private logUserProfileChangeRequest(request: UserProfileChangeRequestImpl) {
    this.log(
      `UpdateClientCommentsRequest - changing to ${request.profileName ?? "<SYSTEM DEFAULT>"}`,
    );
  }

  private logManualNettingRequest(request: ManualNettingRequestImpl) {
    const actionType = ManualNettingAction[request.action];
    const orderInfo = this.getOrderIdsFromLongs(request.orderIds);
    this.log(`ManualNettingRequest - ${actionType} on ${orderInfo}`);
  }

  private logPingTimer(_request: PingTimerImpl) {
    this.log(`PingTimer`);
  }

  // Utility methods

  private getOrderIds(orderIds: string[]): string {
    if (orderIds?.length > 0) {
      return `OrderIds [${orderIds.join(",")}]`;
    }
    return "";
  }

  private getOrderIdsFromLongs(orderIds: Long[]): string {
    if (orderIds?.length > 0) {
      const stringIds = orderIds.map((longId) => longId.toString());
      return `OrderIds [${stringIds.join(",")}]`;
    }
    return "";
  }

  private getOrderId(orderId: string): string {
    if (orderId?.length > 0) {
      return `OrderId[${orderId}]]`;
    }
    return "";
  }

  private addStandardDate(title: string, date: StandardDate) {
    if (date) {
      const day: string | number = date.day < 10 ? `0${date.day}` : date.day;
      const month: string | number = date.month < 10 ? `0${date.month}` : date.month;

      if (title !== null) {
        return `${title} [${date.year}.${month}.${day} (YMD)]`;
      }
      return `${date.year}.${month}.${day} (YMD)`;
    }
    return "";
  }
}
